import React, { useContext, useEffect, useState } from 'react'

import cx from 'classnames'

import { ModalContext } from '../../context/ModalContext'
import useDebug from '../../hooks/useDebug'
import { UserInfo } from '../../hooks/useUserInfo'
import HTTPRequestService from '../../services/HTTPRequestService'
import { bgGreen, cta, selfEnroll } from '../../themes/ctas.module.scss'
import ModalTwo from '../modals/ModalTwo'

type RedirectModalContentProps = {
  redirectURL: string
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RedirectModalContent = ({ redirectURL }: RedirectModalContentProps) => {
  const [redirectCountdown, setRedirectCountdown] = useState(5) // 5 seconds
  useEffect(() => {
    const timerID = setInterval(() => setRedirectCountdown((prevState) => prevState - 1), 1000)
    if (redirectCountdown <= -1) clearTimeout(timerID)
    return () => clearTimeout(timerID)
  })
  useEffect(() => {
    if (redirectURL && redirectCountdown === -1) {
      window.location.href = redirectURL
    }
  }, [redirectCountdown, redirectURL])
  return (
    <div>
      <h2>Success!</h2>
      <p>
        You will be redirected to UMA&apos;s enrollment portal in {redirectCountdown > -1 ? redirectCountdown : 0}
        &nbsp;second
        {redirectCountdown === 1 ? '' : 's'}.
      </p>
    </div>
  )
}

type SelfEnrollCTAProps = {
  /** Runs as soon as the button is clicked, before sending to self enroll. */
  beforeSelfEnroll?: (() => void) | null
  buttonText?: string
  className?: string
  themes?: string[]
  userInfo: UserInfo
}

const SelfEnrollCTA = ({ beforeSelfEnroll, buttonText, className, themes, userInfo }: SelfEnrollCTAProps) => {
  const { closeModal, openModal } = useContext(ModalContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isDebug = useDebug()

  // the modal to display if we receive an unknown error from the API
  const unknownErrorModal = () => (
    <ModalTwo closeModal={closeModal}>
      <div>
        <h2>We&apos;re sorry.</h2>
        <p>
          It looks like our self-enroll feature isn&apos;t working right now. One of our Admissions team members will be
          in touch within 24 hours to confirm your information and to talk through the next steps.
        </p>
      </div>
    </ModalTwo>
  )

  // the modal to display if we receive an email that is already registered.
  const emailErrorModal = (loginLink: string) => (
    <ModalTwo closeModal={closeModal}>
      <div>
        <h2>Email already registered.</h2>
        <p>
          It looks like your email is already registered in our system, so we are unable to forward you to our self
          enrollment platform at this time. That&apos;s OK though, because one of our Admissions team members will be in
          touch within 24 hours to confirm your information and to talk through the next steps.
        </p>
        <a
          href={loginLink}
          className="login-button bg-dark-blue px-5 py-4 d-inline-block"
          style={{ color: 'white', textDecoration: 'none' }}>
          Go to Login
        </a>
      </div>
    </ModalTwo>
  )

  // handle a self enroll click
  const handleClick = async () => {
    // start the submission process
    setIsSubmitting(true)

    // handle before self enroll callback
    if (beforeSelfEnroll) beforeSelfEnroll()

    // handle missing user info
    if (!userInfo) {
      openModal(unknownErrorModal)
      setIsSubmitting(false)
      return
    }

    // create the client
    const client = new HTTPRequestService()

    try {
      // make the request
      const response = await client.post('/.netlify/functions/self-enroll', {
        isDebug,
        userInfo,
      })

      // grab the redirect URL
      const redirectURL = response?.data?.body?.details?.url

      // successful response
      if (redirectURL && response?.data?.body?.details?.code === 'SUCCESS') {
        window.location.href = redirectURL
        // openModal(() => (
        //   <ModalTwo closeModal={closeModal}>
        //     <RedirectModalContent redirectURL={redirectURL} />
        //   </ModalTwo>
        // ))
      }

      // email already in use
      else if (response?.data?.body?.details?.code === 'EMAIL_IN_USE') {
        openModal(() => emailErrorModal(response?.data?.body?.details?.url))
        setIsSubmitting(false)
      }

      // unknown error
      else {
        openModal(unknownErrorModal)
        setIsSubmitting(false)
      }

      // another unknown error
    } catch (e) {
      openModal(unknownErrorModal)
      setIsSubmitting(false)
    }
  }
  return (
    <>
      <button className={cx(className, themes)} type="button" onClick={handleClick} disabled={isSubmitting}>
        {buttonText}
      </button>
    </>
  )
}

SelfEnrollCTA.defaultProps = {
  beforeSelfEnroll: null,
  buttonText: 'Self-Enroll Now',
  className: '',
  themes: [bgGreen, cta, selfEnroll],
}

export default SelfEnrollCTA
